import { clsx } from 'clsx'
import { useRoleStore } from '@/store/role.store'
import { useAuthStore } from '@/store/auth.store'
import type { UserRole } from '@/types/user.types'

const ROLE_LABELS: Record<UserRole, string> = {
  attendee: 'Attendee',
  organiser: 'Organiser',
  vendor: 'Vendor',
  admin: 'Admin',
}

// Per-role active background. Reads the role's accent directly so each
// role keeps its identity colour, even inside the white sidebar.
function activeBgFor(role: UserRole): string {
  if (role === 'vendor') return 'bg-[var(--color-teal)] text-white'
  if (role === 'admin') return 'bg-[var(--color-ember)] text-white'
  // attendee + organiser → gold
  return 'bg-[var(--color-gold)] text-[var(--text-on-accent)]'
}

export default function RoleSwitcher() {
  const { activeRole, setRole } = useRoleStore()
  const roles = useAuthStore((state): UserRole[] => state.user?.roles ?? ['attendee'])

  // Hide the switcher entirely if the user has only one role — saves visual
  // weight in the sidebar for the most common case.
  if (roles.length < 2) return null

  return (
    <div className="flex gap-0.5 bg-[var(--surface-muted)] border border-[var(--border-subtle)] rounded-lg p-0.5">
      {roles.map((role) => (
        <button
          key={role}
          onClick={() => setRole(role)}
          className={clsx(
            'flex-1 py-1.5 rounded-md text-[9px] font-bold tracking-wide uppercase transition-colors',
            activeRole === role
              ? activeBgFor(role)
              : 'text-[var(--text-muted)] hover:text-[var(--text-primary)]',
          )}
        >
          {ROLE_LABELS[role]}
        </button>
      ))}
    </div>
  )
}
